// src/lib/formatResponseTime.ts

/**
 * Options for formatting response time
 */
export interface FormatResponseTimeOptions {
  /** How many samples exist (e.g., benchmark RFQs evaluated). Used for gating. */
  sampleCount?: number;
  /** Minimum samples required to show a real value. Default: 3 */
  minSamples?: number;
  /** If insufficient samples, show "Insufficient data" instead of the value. Default: true */
  showInsufficient?: boolean;
  /** Output format: "full" = "2h 30m", "short" = "2h". Default: "full" */
  format?: "short" | "full";
}

/**
 * Format response time from decimal hours to human-readable string.
 * 
 * @param hours - Response time in decimal hours (e.g., 2.5 = 2h 30m)
 * @param options - Formatting options
 * @returns Formatted string - ALWAYS returns a string, never null/undefined
 * 
 * @example
 * formatResponseTime(2.5) // "2h 30m"
 * formatResponseTime(2.5, { format: "short" }) // "2h"
 * formatResponseTime(null) // "—"
 * formatResponseTime(Infinity) // "N/A"
 * formatResponseTime(2.5, { sampleCount: 1, minSamples: 3 }) // "Insufficient data"
 * formatResponseTime(-1.8) // "< 1m" (clamped to 0)
 */
export function formatResponseTime(
  hours: number | null | undefined,
  options?: FormatResponseTimeOptions
): string {
  const {
    sampleCount,
    minSamples = 3,
    showInsufficient = true,
    format = "full",
  } = options ?? {};

  // Null/undefined -> dash
  if (hours === null || hours === undefined) {
    return "—";
  }

  // Invalid numbers (NaN, Infinity, -Infinity) -> N/A
  if (!Number.isFinite(hours)) {
    return "N/A";
  }

  // Insufficient sample gating (only when sampleCount is explicitly provided)
  if (
    showInsufficient &&
    typeof sampleCount === "number" &&
    sampleCount >= 0 &&
    sampleCount < minSamples
  ) {
    return "Insufficient data";
  }

  // Clamp negative to 0 (fixes the "-1.8h" bug)
  const safeHours = Math.max(0, hours);

  // Convert to total minutes for formatting
  const totalMinutes = Math.round(safeHours * 60);

  // Edge case: very small or zero
  if (totalMinutes <= 0) {
    return "< 1m";
  }

  // Under 1 hour: show minutes only
  if (totalMinutes < 60) {
    return `${totalMinutes}m`;
  }

  // Calculate hours and remaining minutes
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  // Short format or no minutes: hours only
  if (format === "short" || m === 0) {
    return `${h}h`;
  }

  // Full format with minutes
  return `${h}h ${m}m`;
}
